import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, IconButton, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { navigationSettingsState, navigationTranslationState } from 'store';
import { showChoicePopupSearchField } from 'store/atoms/navigation/choicepopup';
import {
  Background,
  Wrapper,
  Divider,
  PathTypeContainer,
  PathTypeItem,
  PathTypeSkeletonItem,
  PathTypeItemsContainer,
  LoadingButton,
  generateCollapseStyle,
  choicePopupPointBtn,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/styles';
import DeviceManager from '@/components/util/deviceManager';
import {
  ChevronLeftIcon,
  CrossIcon,
  DotsIcon,
  ElevatorIcon,
  LocationIcon,
  PhoneIcon,
  PlayIcon,
  RedCircleIcon,
  SearchIcon,
  StairsIcon,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { FilterDirectionButton } from '@/components/navigation/search-bar/components/FilterDirectionButton';
import { SortDirectionButton } from '@/components/navigation/search-bar/components/SortDirrectionButton';
import { DetailedRoutes } from './DetailedRoutes';
import { ActionTooltip } from './ActionTooltip';
import { convertDestinationItemToSweep } from '@/components/util';
import { TeleportGroupType } from '@/components/navigation/NavigationWrapper';
import { ChoicePopupProps } from './types';
import { renderIcon, trimDescription as trimText } from '@/components/navigation/search-bar/util';
import { TourType } from '@/_models/Tour';

export enum NavigationType {
  Shortest = 'shortest',
  NoElevators = 'noElevators',
  NoStairs = 'noStairs',
}

export const NavigationIntroChoicePopup: FC<ChoicePopupProps> = ({
  open,
  setOpen,
  item,
  contentLoaded,
  loadingProgress,
  onConfirm,
  onReturn,
  rtl,
  startingPoint,
  onSetFinishPoint,
  menuItems,
  selectedFilters,
  setSelectedFilters,
  onSetStartingPoint,
  startSweep,
  choicePopupWrapper,
  choicePopupSearchBar,
  choicePopupFilteredBtn,
  sharingEnabled,
  setSharePopupOpen,
  sharePopupOpen,
  setOpenNavigationSharingPopup,
  setNavigationDestination,
  target,
  detailedRoutes,
  allSweeps,
  startingPointTitle,
  finishPointTitle,
  allTags,
  teleportPoints,
  selectedNavigationType,
  setSelectedNavigationType,
  mobilePosition,
  navigationType,
  filteredItems,
  searchText,
  setSearchText,
  setSortingValue,
  setSelectedItem,
  setMobilePosition,
  tourType,
}) => {
  const [isVisible, setIsVisible] = useState(open);
  const [isFinishItems, setIsFinishItems] = useState(false);
  const translation = useRecoilValue(navigationTranslationState);
  const navigationSettings = useRecoilValue(navigationSettingsState);
  const [showSearchField, setShowSearchField] = useRecoilState(showChoicePopupSearchField);

  const { defaultSorting, detailedRouteEnabled } = navigationSettings;
  const inputRef = useRef(null);
  const isMobile = DeviceManager.isMobile();
  const elevators = useMemo(
    () => teleportPoints.filter((tg) => tg.type === TeleportGroupType.Elevator && tg?.pointNodes?.length > 0).flatMap((tg) => tg.pointNodes),
    [teleportPoints],
  );
  const stairs = useMemo(
    () => teleportPoints.filter((tg) => tg.type === TeleportGroupType.Stairway && tg?.pointNodes?.length > 0).flatMap((tg) => tg.pointNodes),
    [teleportPoints],
  );

  useEffect(() => {
    if (!elevators?.length && stairs?.length > 0) {
      setSelectedNavigationType(NavigationType.NoElevators);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevators, stairs]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectStartItem = (selectedItem: any) => {
    const startingItem = convertDestinationItemToSweep({ item: selectedItem, allSweeps: Array.from(allSweeps.values()), allTags: Array.from(allTags.values()) });
    onSetStartingPoint(startingItem);
    setShowSearchField(false);
  };

  const selectFinishItem = (selectedItem: any) => {
    const finishingItem = convertDestinationItemToSweep({ item: selectedItem, allSweeps: Array.from(allSweeps.values()), allTags: Array.from(allTags.values()) });
    onSetFinishPoint(finishingItem);
    setShowSearchField(false);
    setIsFinishItems(false);
  };

  useEffect(() => {
    if (defaultSorting) {
      setSortingValue(defaultSorting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSorting]);

  if (!item) return null;

  const handleBackClick = () => {
    if (!showSearchField) {
      setOpen(false);
      setSelectedNavigationType(NavigationType.Shortest);
      setShowSearchField(false);
      onReturn();
    } else {
      setShowSearchField(false);
      setIsFinishItems(false);
    }
    if (isMobile) setMobilePosition(1);
  };

  const handleCloseClick = () => {
    setSelectedItem(null);
    setOpen(false);
    setShowSearchField(false);
    setSelectedNavigationType(NavigationType.Shortest);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setSearchText(event.target.value);
      inputRef.current?.blur();
    }
  };

  return (
    <>
      <Background
        open={open}
        isVisible={isVisible}
        style={{
          background: 'none',
          backdropFilter: 'blur(0px)',
          position: 'static',
          marginTop: DeviceManager.isMobile() ? 0 : '',
          height: 'auto',
          display: open ? '' : 'none',
          width: DeviceManager.isMobile() ? '100%' : 'fit-content',
        }}
      >
        <Wrapper
          open={open}
          onClick={(e) => e.stopPropagation()}
          // @ts-ignore
          style={{ ...choicePopupWrapper, display: sharePopupOpen ? 'none' : '' }}
        >
          <Stack direction="row" sx={{ p: { xs: '0 14px 10px 14px', sm: '14px' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <IconButton
              sx={{ visibility: { xs: 'hidden', sm: 'visible' }, borderRadius: '1rem', padding: '4px', background: '#EAECF0', width: '32px', height: '32px' }}
              onClick={handleBackClick}
            >
              <ChevronLeftIcon style={{}} />
            </IconButton>
            <Typography sx={{ direction: rtl ? 'rtl' : 'ltr' }}>{translation?.navDirections || 'Directions'}</Typography>
            <IconButton
              style={{ borderRadius: '1rem', padding: '4px', background: '#EAECF0', width: '32px', height: '32px' }}
              onClick={isMobile ? handleBackClick : handleCloseClick}
            >
              <CrossIcon style={{ width: '18px', height: '18px' }} />
            </IconButton>
          </Stack>
          <Divider style={{ margin: '0px -24px' }} />
          {!showSearchField && (
            <Stack className={!showSearchField ? 'show' : 'hide'}>
              <Stack padding="14px 14px 20px 14px">
                <Stack direction="row" justifyContent="space-around">
                  <Stack width="100%" mr="6px">
                    <IconButton
                      disabled={tourType === TourType.Kiosk}
                      sx={{ ...choicePopupPointBtn, direction: rtl ? 'rtl' : 'ltr' }}
                      onClick={() => {
                        setSearchText(startingPointTitle);
                        setShowSearchField(true);
                        if (isMobile) setMobilePosition(2);
                      }}
                    >
                      {startingPoint && renderIcon(startingPoint, 30, 30)}
                      <Typography color="#101828" sx={{ direction: rtl ? 'rtl' : 'ltr' }}>{trimText(startingPointTitle, 25)}</Typography>
                    </IconButton>
                    <IconButton
                      style={{
                        marginTop: '10px',
                        direction: rtl ? 'rtl' : 'ltr',
                        ...choicePopupPointBtn,
                      }}
                      onClick={() => {
                        setSearchText(finishPointTitle);
                        setShowSearchField(true);
                        setIsFinishItems(true);
                        if (isMobile) setMobilePosition(2);
                      }}
                    >
                      {item && renderIcon(item, 30, 30)}
                      <Typography color="#101828" sx={{ direction: rtl ? 'rtl' : 'ltr' }}>{trimText(finishPointTitle, 25)}</Typography>
                    </IconButton>
                  </Stack>
                  <Stack alignItems="center" gap="8px" height="110px" justifyContent="center">
                    <RedCircleIcon style={{ width: '14px', height: '14px' }} />
                    <DotsIcon style={{ width: '18px', height: '35px' }} />
                    <LocationIcon style={{ width: '14px', height: '18px' }} />
                  </Stack>
                </Stack>
                {teleportPoints?.length > 0 && elevators?.length > 0 && (
                  <PathTypeContainer rtl={rtl}>
                    <PathTypeItemsContainer>
                      {contentLoaded ? (
                        <>
                          <PathTypeItem
                            rtl={rtl}
                            selected={selectedNavigationType === NavigationType.Shortest}
                            onClick={() => {
                              setSelectedNavigationType(NavigationType.Shortest);
                            }}
                          >
                            <ElevatorIcon style={{ width: '20px', height: '20px', margin: '8px 0' }} />
                            <Typography fontSize="16px" fontWeight={rtl ? 400 : 500} sx={{ direction: rtl ? 'rtl' : 'ltr' }}>{translation?.navElevatorRoute || 'Elevator Route'}</Typography>
                          </PathTypeItem>
                          <PathTypeItem
                            rtl={rtl}
                            selected={selectedNavigationType === NavigationType.NoElevators || selectedNavigationType === NavigationType.NoStairs}
                            onClick={() => {
                              const navType = stairs?.length > 0 ? NavigationType.NoElevators : NavigationType.NoStairs;
                              setSelectedNavigationType(navType);
                            }}
                            style={{ flex: 1 }}
                          >
                            <StairsIcon
                              style={{ width: '20px', height: '20px', margin: '8px 0' }}
                              color={selectedNavigationType === NavigationType.NoElevators || selectedNavigationType === NavigationType.NoStairs ? '#2055ff' : 'black'}
                            />
                            <Typography fontSize="16px" fontWeight={rtl ? 400 : 500} sx={{ direction: rtl ? 'rtl' : 'ltr' }}>{translation?.navStairOnly || 'Stairs Only'}</Typography>
                          </PathTypeItem>
                        </>
                      ) : (
                        <>
                          <PathTypeSkeletonItem selected>
                            <Skeleton variant="circular" width={20} height={20} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                          </PathTypeSkeletonItem>
                          <PathTypeSkeletonItem>
                            <Skeleton variant="circular" width={20} height={20} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                          </PathTypeSkeletonItem>
                          <PathTypeSkeletonItem>
                            <Skeleton variant="circular" width={20} height={20} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                            <Skeleton variant="text" sx={{ fontSize: isMobile ? '14px' : '16px' }} />
                          </PathTypeSkeletonItem>
                        </>
                      )}
                    </PathTypeItemsContainer>
                  </PathTypeContainer>
                )}
                {!sharePopupOpen && (
                  <div style={{ display: isMobile ? 'none' : 'initial', marginTop: !elevators?.length && !stairs?.length ? '10px' : '0px' }}>
                    <ActionTooltip detailedRouteEnabled={detailedRouteEnabled} sharingPopupOpen={sharePopupOpen} showSearchField={showSearchField} rtl={rtl}>
                      <LoadingButton
                        style={{ direction: rtl ? 'rtl' : 'ltr', fontWeight: rtl ? 500 : 600, fontFamily: 'Inter, Heebo, sans-serif' }}
                        onClick={() => {
                          onConfirm(selectedNavigationType);
                          setOpenNavigationSharingPopup(true);
                        }}
                        disabled={loadingProgress < 100}
                        loadingProgress={loadingProgress}
                      >
                        <PlayIcon style={{ width: '20px', height: '20px' }} />
                        {loadingProgress < 100 ? `${translation?.navLoading || 'Loading'} ${loadingProgress}%` : `${translation?.navStart3DNavigation || 'Start 3D Navigation'}`}
                      </LoadingButton>
                    </ActionTooltip>
                    {sharingEnabled && (
                      <IconButton
                        sx={{
                          display: 'flex',
                          width: '100%',
                          borderRadius: '8px',
                          border: '1px solid #D0D5DD',
                          color: '#2055FF',
                          gap: '6px',
                          background: '#ffff',
                          marginTop: '10px',
                          '&:hover': { background: '#ffff' },
                          direction: rtl ? 'rtl' : 'ltr',
                        }}
                        onClick={() => setSharePopupOpen(true)}
                      >
                        <PhoneIcon style={{ width: '20px', height: '20px' }} />
                        <Typography lineHeight="21px" fontWeight={rtl ? 500 : 600}>
                          {translation.navShareToPhone || 'Share to your phone'}
                        </Typography>
                      </IconButton>
                    )}
                  </div>
                )}
              </Stack>
            </Stack>
          )}
          {showSearchField && (
            <Stack className={showSearchField ? 'show' : 'hide'}>
              <Stack>
                <Stack padding="14px 14px 10px 14px">
                  <TextField
                    inputRef={inputRef}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: (
                        <IconButton sx={{ width: '32px', height: '32px', padding: '0px', '&:hover': { background: '#ffff' } }}>
                          <SearchIcon style={{ width: '21px', height: '21px' }} />
                        </IconButton>
                      ),
                      endAdornment: (
                        <IconButton style={{ width: '32px', height: '32px', padding: '0px' }} onClick={() => setSearchText('')}>
                          <CrossIcon style={{ width: '21px', height: '21px' }} />
                        </IconButton>
                      ),
                    }}
                    sx={{ ...choicePopupSearchBar, direction: rtl ? 'rtl' : 'ltr' }}
                  />
                </Stack>
                <Stack padding="10px 14px">
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ direction: rtl ? 'rtl' : 'ltr' }}>
                    <Stack direction="row" spacing="10px" alignContent="center" justifyContent="center">
                      <IconButton
                        sx={{ display: { xs: 'inline-flex', sm: 'none' }, borderRadius: '1rem', padding: '4px', background: '#EAECF0', width: '32px', height: '32px' }}
                        onClick={handleBackClick}
                      >
                        <ChevronLeftIcon style={{}} />
                      </IconButton>
                      <Typography display="inline-flex" alignItems="center" lineHeight="24px" fontSize="16px" color="#101828">
                        {translation.navResults || 'Results'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap="10px" sx={{ direction: rtl ? 'ltr' : '' }}>
                      <FilterDirectionButton menuItems={menuItems} selectedFilters={selectedFilters} setSelectedFilters={setSelectedFilters} />
                      <SortDirectionButton setSortingValue={setSortingValue} />
                    </Stack>
                  </Stack>
                </Stack>
                <Divider style={{ margin: '0px -24px' }} />
                <Collapse
                  in={filteredItems?.length !== 0}
                  sx={generateCollapseStyle({ filteredItems, mobilePosition, rtl })}
                  className={filteredItems?.length === 1 ? 'animateHeight' : ''}
                >
                  {filteredItems?.map((filteredItem: any, i) => {
                    return (
                      <IconButton
                        key={i}
                        sx={choicePopupFilteredBtn}
                        onClick={() => {
                          if (!isFinishItems) {
                            if (filteredItem.sweepId === startSweep.sweepId) {
                              setShowSearchField(false);
                            }
                            selectStartItem(filteredItem);
                          } else selectFinishItem(filteredItem);
                        }}
                      >
                        {filteredItem && renderIcon(filteredItem, 38, 38)}
                        <Stack sx={{ textAlign: 'start' }}>
                          <Typography fontSize="16px" fontWeight="400">
                            {trimText(filteredItem?.title || filteredItem?.tag?.title || filteredItem?.name, 25)}
                          </Typography>
                          <span style={{ fontSize: '14px', fontWeight: 300, lineHeight: '21.5px' }}>{trimText(filteredItem?.category?.title, 25)}</span>
                        </Stack>
                      </IconButton>
                    );
                  })}
                </Collapse>
              </Stack>
            </Stack>
          )}
          {detailedRouteEnabled && !showSearchField && (
            <DetailedRoutes
              item={item || { tag: target }}
              setNavigationDestination={setNavigationDestination}
              isPreload
              detailedRoutes={detailedRoutes}
              startingPoint={startingPoint}
              rtl={rtl}
              navigationType={navigationType}
            />
          )}
        </Wrapper>
      </Background>
      {!showSearchField && isMobile && (
        <Stack
          id="nav-start-btn-container"
          sx={{
            position: isMobile ? 'absolute' : 'initial',
            bottom: 0,
            left: isMobile ? 0 : undefined,
            background: isMobile ? '#fff' : 'transparent',
            width: isMobile ? '100%' : 'auto',
            p: isMobile ? '14px 12px 30px 12px' : '14px',
            zIndex: isMobile ? 9999 : 'initial',
          }}
        >
          <LoadingButton
            onClick={() => {
              onConfirm(selectedNavigationType);
              setSelectedNavigationType(NavigationType.Shortest);
              setOpenNavigationSharingPopup(true);
            }}
            disabled={loadingProgress < 100}
            loadingProgress={loadingProgress}
          >
            <PlayIcon style={{ width: '20px', height: '20px' }} />
            {loadingProgress < 100 ? `${translation?.navLoading || 'Loading'} ${loadingProgress}%` : `${translation.navStart3DNavigation || 'Start 3D Navigation'}`}
          </LoadingButton>
        </Stack>
      )}
    </>
  );
};
